import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  alpha,
  Avatar,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

interface DashboardCardProps {
  title: string;
  description?: string;
  subtitle?: string;
  to?: string;
  icon?: React.ReactNode;
  color: string;
  loading?: boolean;
}

const StatCard = ({
  title,
  description,
  to,
  icon,
  color,
  loading = false,
  subtitle,
}: DashboardCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleClick = () => {
    if (to) navigate(to);
  };

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: "all 0.2s ease",
          cursor: to ? "pointer" : "initial",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="60%" height={16} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.2s ease",
        cursor: to ? "pointer" : "initial",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          backgroundColor: color,
          transition: "all 0.2s ease",
        },
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.08)}`,
          "&::before": {
            width: 6,
          },
        },
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.15),
              color: color,
              width: 36,
              height: 36,
              transition: "all 0.2s ease",
            }}
          >
            {icon}
          </Avatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "0.9rem",
                color: theme.palette.text.primary,
                mb: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {title}
            </Typography>
            
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1.1rem",
                color: theme.palette.text.secondary,
                lineHeight: 1.2,
                mb: subtitle ? 0.25 : 0,
              }}
            >
              {description}
            </Typography>
            
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.text.secondary,
                  opacity: 0.8,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block"
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatCard;